import { useEffect } from 'react';
import { classNames } from './class-names.js';
import { useConsoleServices } from './console-services.jsx';
import type { ConsoleConnection, ConsolePhoneNumber } from './console-api.js';
import type { StepViewProps } from './flow-route-types.js';
import { Notice } from './notice.jsx';
import { paths } from './paths.js';
import { useOnboarding } from './use-onboarding.jsx';
import { useWhatsappConnection, type NumbersStatus } from './use-whatsapp-connection.js';

export function StepWhatsappConnect({ onNavigate }: StepViewProps) {
  const { connection, draft } = useOnboarding();
  const services = useConsoleServices();
  const controller = useWhatsappConnection(services);
  const { loadNumbers, selectNumber: persistSelectedNumber } = controller;
  const needsSelectedNumberHydration = needsPhoneNumberHydration(connection);
  const shouldLoadNumbers = Boolean(
    connection && (!connection.phoneNumberId || needsSelectedNumberHydration),
  );

  useEffect(() => {
    if (!shouldLoadNumbers) {
      return;
    }

    void loadNumbers();
  }, [shouldLoadNumbers, loadNumbers]);

  useEffect(() => {
    if (!connection) {
      return;
    }

    if (!needsSelectedNumberHydration) {
      return;
    }

    if (controller.numbersStatus !== 'pronto') {
      return;
    }

    const selectedNumber = selectedNumberForConnection(connection, controller.numbers);

    if (!selectedNumber) {
      return;
    }

    void persistSelectedNumber(selectedNumber);
  }, [
    connection,
    controller.numbers,
    controller.numbersStatus,
    needsSelectedNumberHydration,
    persistSelectedNumber,
  ]);

  async function selectNumber(number: ConsolePhoneNumber) {
    const selected = await controller.selectNumber(number);

    if (!selected) {
      return;
    }

    onNavigate(paths.review);
  }

  if (connection?.phoneNumberId) {
    if (needsSelectedNumberHydration) {
      return (
        <div className="flex flex-col gap-6">
          <ConnectError message={controller.connectError} />
          <ConnectedAccountCard connection={connection} />
          <SelectedPhoneHydration
            connection={connection}
            numbers={controller.numbers}
            numbersStatus={controller.numbersStatus}
            numbersError={controller.numbersError}
            onRetry={loadNumbers}
            onSelect={selectNumber}
          />
          <div>
            <button className="btn btn-ghost" type="button" onClick={() => void controller.disconnect()}>
              Desconectar
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-6">
        <ConnectError message={controller.connectError} />
        <SelectedPhoneCard connection={connection} businessName={connectedBusinessName(connection, draft.store.name)} />
        <div className="flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
          <button className="btn btn-ghost" type="button" onClick={() => void controller.disconnect()}>
            Desconectar
          </button>
          <button className="btn btn-primary" type="button" onClick={() => onNavigate(paths.review)}>
            Continuar para revisão
          </button>
        </div>
      </div>
    );
  }

  if (connection) {
    return (
      <div className="flex flex-col gap-6">
        <ConnectError message={controller.connectError} />
        <ConnectedAccountCard connection={connection} />
        <NumberSelection
          numbers={controller.numbers}
          numbersStatus={controller.numbersStatus}
          numbersError={controller.numbersError}
          selectedPhoneNumberId={connection.phoneNumberId}
          onRetry={loadNumbers}
          onSelect={selectNumber}
        />
        <div>
          <button className="btn btn-ghost" type="button" onClick={() => void controller.disconnect()}>
            Desconectar
          </button>
        </div>
      </div>
    );
  }

  if (!services.signup?.isConfigured()) {
    return (
      <Notice tone="warning" title="Conexão com o WhatsApp indisponível">
        Não é possível conectar sua conta Meta Business agora. Tente de novo mais tarde ou fale com
        a Marketside.
      </Notice>
    );
  }

  if (controller.connectStatus === 'conectando') {
    return (
      <p className="flex items-center gap-3" role="status">
        <span className="loading loading-spinner loading-sm" aria-hidden="true" />
        Abrindo o login da Meta…
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <ConnectError message={controller.connectError} />

      <section className="rounded-box border border-base-300 p-4">
        <h2 className="font-semibold">Antes de conectar</h2>
        <ul className="mt-3 flex flex-col gap-3 text-sm opacity-80">
          <li className="flex gap-3">
            <span className="mt-2 size-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
            <span>Você precisa ser administrador da conta Meta Business da loja.</span>
          </li>
          <li className="flex gap-3">
            <span className="mt-2 size-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
            <span>Tenha disponível o telefone de WhatsApp que a loja vai usar.</span>
          </li>
          <li className="flex gap-3">
            <span className="mt-2 size-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
            <span>Nada é ativado antes da confirmação final do teste grátis.</span>
          </li>
          <li className="flex gap-3">
            <span className="mt-2 size-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
            <span>Tudo acontece no navegador pelo Meta Embedded Signup padrão.</span>
          </li>
        </ul>
      </section>

      <section className="rounded-box border border-base-300 p-4">
        <h2 className="font-semibold">Entrar com a Meta</h2>
        <p className="mt-2 text-sm opacity-70">
          Você entra na conta Meta Business e autoriza a Marketside a usar o WhatsApp da loja.
        </p>
        <div className="mt-4">
          <button className="btn btn-primary" type="button" onClick={() => void controller.connect()}>
            Conectar conta Meta Business
          </button>
        </div>
      </section>
    </div>
  );
}

function ConnectedAccountCard({ connection }: { connection: ConsoleConnection }) {
  return (
    <section className="rounded-box border border-base-300 p-4">
      <div className="flex items-center gap-2">
        <span className="badge badge-success badge-sm">Conta Conectada</span>
      </div>
      <p className="mt-3 font-medium">{connection.verifiedName ?? 'Conta Meta Business'}</p>
      <p className="mt-1 text-sm opacity-70">{accountHint(connection)}</p>
    </section>
  );
}

function SelectedPhoneCard({
  connection,
  businessName,
}: {
  connection: ConsoleConnection;
  businessName: string;
}) {
  return (
    <section className="rounded-box overflow-hidden border border-primary/30 bg-primary/5 shadow-sm">
      <div className="border-b border-primary/20 bg-base-100/80 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="badge badge-success badge-sm">Conta Conectada</span>
          <span className="badge badge-ghost badge-sm">Meta Business</span>
        </div>
      </div>

      <div className="flex flex-col gap-5 p-4 sm:flex-row sm:items-start">
        <div className="flex size-14 shrink-0 items-center justify-center rounded-full bg-primary text-xl font-semibold text-primary-content">
          {profileInitial(businessName)}
        </div>

        <div className="min-w-0 flex-1">
          <div>
            <h2 className="truncate text-lg font-semibold">{businessName}</h2>
            <p className="mt-1 font-mono text-sm font-medium">{selectedPhoneNumberLabel(connection)}</p>
          </div>

          <div className="mt-4 flex flex-wrap gap-2" aria-label="Ações do perfil">
            <span className="badge badge-outline">Ligar</span>
            <span className="badge badge-outline">Catálogo</span>
            <span className="badge badge-outline">Compartilhar</span>
          </div>

          <dl className="mt-5 grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide opacity-60">Setor</dt>
              <dd className="mt-1 font-medium">Mercado e alimentos</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide opacity-60">Site</dt>
              <dd className="mt-1">
                <a className="link link-primary font-medium" href="https://marketside.com.br">
                  marketside.com.br
                </a>
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </section>
  );
}

function SelectedPhoneHydration({
  connection,
  numbers,
  numbersStatus,
  numbersError,
  onRetry,
  onSelect,
}: {
  connection: ConsoleConnection;
  numbers: ConsolePhoneNumber[];
  numbersStatus: NumbersStatus;
  numbersError: string | null;
  onRetry: () => Promise<void>;
  onSelect: (number: ConsolePhoneNumber) => void;
}) {
  if (numbersStatus === 'ocioso') {
    return <LoadingSelectedPhoneNumber />;
  }

  if (numbersStatus === 'carregando') {
    return <LoadingSelectedPhoneNumber />;
  }

  if (numbersStatus === 'erro') {
    return <SelectedPhoneHydrationError message={numbersError} onRetry={onRetry} />;
  }

  const selectedNumber = selectedNumberForConnection(connection, numbers);

  if (selectedNumber) {
    if (numbersError) {
      return (
        <div className="flex flex-col gap-6">
          <Notice tone="danger" title="Não foi possível confirmar o número escolhido">
            {numbersError}
          </Notice>
          <div>
            <button className="btn btn-primary" type="button" onClick={() => void onRetry()}>
              Tentar novamente
            </button>
          </div>
          <NumberSelection
            numbers={numbers}
            numbersStatus={numbersStatus}
            numbersError={null}
            selectedPhoneNumberId={connection.phoneNumberId}
            onRetry={onRetry}
            onSelect={onSelect}
          />
        </div>
      );
    }

    return <LoadingSelectedPhoneNumber />;
  }

  if (numbers.length === 0) {
    return (
      <div className="flex flex-col gap-6">
        <Notice tone="warning" title="Não encontramos o número escolhido">
          A Meta confirmou um número, mas ele não apareceu na lista desta conta. Atualize a lista ou
          revise a conta Meta Business conectada.
        </Notice>
        <div>
          <button className="btn btn-primary" type="button" onClick={() => void onRetry()}>
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Notice tone="warning" title="Não encontramos o número escolhido">
        A Meta confirmou um número, mas ele não apareceu na lista desta conta. Escolha abaixo o
        número da loja ou atualize a lista.
      </Notice>
      <NumberSelection
        numbers={numbers}
        numbersStatus={numbersStatus}
        numbersError={null}
        selectedPhoneNumberId={connection.phoneNumberId}
        onRetry={onRetry}
        onSelect={onSelect}
      />
    </div>
  );
}

function SelectedPhoneHydrationError({
  message,
  onRetry,
}: {
  message: string | null;
  onRetry: () => Promise<void>;
}) {
  return (
    <div className="flex flex-col gap-6">
      <Notice tone="danger" title="Não foi possível carregar o número escolhido">
        {selectedPhoneHydrationErrorMessage(message)}
      </Notice>
      <div>
        <button className="btn btn-primary" type="button" onClick={() => void onRetry()}>
          Tentar novamente
        </button>
      </div>
    </div>
  );
}

function LoadingSelectedPhoneNumber() {
  return (
    <p className="flex items-center gap-3" role="status">
      <span className="loading loading-spinner loading-sm" aria-hidden="true" />
      Carregando número escolhido…
    </p>
  );
}

function NumberSelection({
  numbers,
  numbersStatus,
  numbersError,
  selectedPhoneNumberId,
  onRetry,
  onSelect,
}: {
  numbers: ConsolePhoneNumber[];
  numbersStatus: string;
  numbersError: string | null;
  selectedPhoneNumberId: string | null;
  onRetry: () => Promise<void>;
  onSelect: (number: ConsolePhoneNumber) => void;
}) {
  if (numbersStatus === 'ocioso') {
    return <LoadingNumbers />;
  }

  if (numbersStatus === 'carregando') {
    return <LoadingNumbers />;
  }

  if (numbersStatus === 'erro') {
    return (
      <div className="flex flex-col gap-6">
        <Notice tone="danger" title="Não foi possível listar os números">
          {numbersError}
        </Notice>
        <div>
          <button className="btn btn-primary" type="button" onClick={() => void onRetry()}>
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  if (numbers.length === 0) {
    return (
      <Notice tone="warning" title="Nenhum número disponível">
        A conta conectada não tem nenhum número de WhatsApp disponível. Cadastre um número na sua
        conta Meta Business e volte aqui.
      </Notice>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <SelectNumberError message={numbersError} />
      <fieldset className="fieldset w-full">
        <legend className="fieldset-legend">Escolha o número da loja</legend>
        <div className="flex flex-col gap-2">
          {numbers.map((number) => (
            <NumberOption
              key={number.id}
              number={number}
              selected={selectedPhoneNumberId === number.id}
              onSelect={() => onSelect(number)}
            />
          ))}
        </div>
      </fieldset>
    </div>
  );
}

function SelectNumberError({ message }: { message: string | null }) {
  if (!message) {
    return null;
  }

  return (
    <Notice tone="danger" title="Não foi possível escolher o número">
      {message}
    </Notice>
  );
}

function NumberOption({
  number,
  selected,
  onSelect,
}: {
  number: ConsolePhoneNumber;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <label
      className={classNames(
        'flex cursor-pointer items-start gap-3 rounded-field border p-3 transition-colors',
        selected && 'border-primary bg-primary/5',
        !selected && 'border-base-300 hover:border-primary/40',
      )}
    >
      <input
        type="radio"
        className="radio radio-primary radio-sm mt-1"
        name="phoneNumberId"
        checked={selected}
        onChange={onSelect}
      />
      <span className="flex min-w-0 flex-col gap-1">
        <span className="font-mono font-medium">{number.displayPhoneNumber}</span>
        <span className="text-sm opacity-70">{number.verifiedName ?? 'Nome ainda não verificado'}</span>
        <span className="flex flex-wrap gap-2">
          <VerificationBadge status={number.codeVerificationStatus} />
        </span>
      </span>
    </label>
  );
}

function LoadingNumbers() {
  return (
    <p className="flex items-center gap-3" role="status">
      <span className="loading loading-spinner loading-sm" aria-hidden="true" />
      Buscando os números da sua conta…
    </p>
  );
}

function ConnectError({ message }: { message: string | null }) {
  if (!message) {
    return null;
  }

  return (
    <Notice tone="danger" title="Não foi possível conectar">
      {message}
    </Notice>
  );
}

function VerificationBadge({ status }: { status: string | null }) {
  if (status === 'VERIFIED') {
    return <span className="badge badge-sm badge-success">Verificado</span>;
  }

  return <span className="badge badge-sm badge-ghost">Verificação pendingStep</span>;
}

function accountHint(connection: ConsoleConnection): string {
  if (connection.phoneNumberId) {
    return 'O WhatsApp está conectado e já tem um número escolhido.';
  }

  return 'Escolha abaixo o número que vai atender seus clientes.';
}

function connectedBusinessName(connection: ConsoleConnection, storeName: string): string {
  const verifiedName = connection.verifiedName?.trim();

  if (verifiedName) {
    return verifiedName;
  }

  const draftStoreName = storeName.trim();

  if (draftStoreName) {
    return draftStoreName;
  }

  return 'Conta Meta Business';
}

function profileInitial(name: string): string {
  const trimmedName = name.trim();

  if (trimmedName) {
    return trimmedName.slice(0, 1).toUpperCase();
  }

  return 'M';
}

function selectedPhoneNumberLabel(connection: ConsoleConnection): string {
  if (connection.displayPhoneNumber) {
    return connection.displayPhoneNumber;
  }

  return 'Número escolhido pendingStep de confirmação';
}

function needsPhoneNumberHydration(connection: ConsoleConnection | null): boolean {
  if (!connection?.phoneNumberId) {
    return false;
  }

  if (connection.displayPhoneNumber) {
    return false;
  }

  return true;
}

function selectedNumberForConnection(
  connection: ConsoleConnection,
  numbers: ConsolePhoneNumber[],
): ConsolePhoneNumber | null {
  if (!connection.phoneNumberId) {
    return null;
  }

  return numbers.find((number) => number.id === connection.phoneNumberId) ?? null;
}

function selectedPhoneHydrationErrorMessage(message: string | null): string {
  if (message) {
    return message;
  }

  return 'Não conseguimos buscar os números da conta Meta. Tente novamente em instantes.';
}

